import { useEffect, useState } from "react";
import { ApiError } from "../../../api/http";
import {
  fetchNotificationPreferences,
  NOTIFICATION_CATEGORIES,
  putNotificationPreferences,
  type NotificationPreference,
} from "../../../api/staffApi";
import { categoryLabel } from "../staffFormat";
import { StaffAlert } from "../components/StaffAlert";
import { StaffAppShell } from "../components/StaffAppShell";
import { SkeletonCard } from "../components/SkeletonCard";

type PrefRow = { category: string; inAppEnabled: boolean; pushEnabled: boolean };

function mergePrefs(existing: NotificationPreference[]): PrefRow[] {
  const byCat = new Map(existing.map((p) => [p.category, p]));
  return NOTIFICATION_CATEGORIES.map((category) => {
    const row = byCat.get(category);
    return {
      category,
      inAppEnabled: row?.inAppEnabled ?? true,
      pushEnabled: row?.pushEnabled ?? false,
    };
  });
}

export function StaffNotificationPreferencesPage() {
  const [prefs, setPrefs] = useState<PrefRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchNotificationPreferences();
        setPrefs(mergePrefs(data));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : "Could not load preferences.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await putNotificationPreferences(prefs);
      setPrefs(mergePrefs(updated));
      setSuccess("Preferences saved.");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StaffAppShell title="Notification preferences" showBack backTo="/staff/me" hideNav>
      <div className="staff-page">
        {error ? <StaffAlert onDismiss={() => setError(null)}>{error}</StaffAlert> : null}
        {success ? (
          <StaffAlert variant="success" onDismiss={() => setSuccess(null)}>{success}</StaffAlert>
        ) : null}

        {loading ? (
          <SkeletonCard />
        ) : (
          <>
            <ul className="staff-pref-list">
              {prefs.map((p) => (
                <li key={p.category} className="staff-pref-row">
                  <span className="staff-pref-row__label">{categoryLabel(p.category)}</span>
                  <label className="staff-pref-toggle">
                    <input
                      type="checkbox"
                      checked={p.inAppEnabled}
                      onChange={(e) =>
                        setPrefs((prev) =>
                          prev.map((row) =>
                            row.category === p.category ? { ...row, inAppEnabled: e.target.checked } : row,
                          ),
                        )
                      }
                    />
                    In-app
                  </label>
                  <label className="staff-pref-toggle">
                    <input
                      type="checkbox"
                      checked={p.pushEnabled}
                      onChange={(e) =>
                        setPrefs((prev) =>
                          prev.map((row) =>
                            row.category === p.category ? { ...row, pushEnabled: e.target.checked } : row,
                          ),
                        )
                      }
                    />
                    Push
                  </label>
                </li>
              ))}
            </ul>
            <button type="button" className="primary-button staff-full-btn" disabled={saving} onClick={() => void save()}>
              {saving ? "Saving…" : "Save preferences"}
            </button>
          </>
        )}
      </div>
    </StaffAppShell>
  );
}
